import { useMemo } from 'react';

import {
  DEFAULT_CATALOG_STATE,
  useGetProductCatalogQuery,
} from '@/api/hooks/queries/use-get-product-catalog-query';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import type { Product } from '@/types/product.type';

type UseReferrerProductResult = {
  referrerProductId: string | null;
  product: Product | undefined;
  isLoading: boolean;
  isNotFound: boolean;
};

export function useReferrerProduct(): UseReferrerProductResult {
  const referrerProductId = useSessionStore((state) => state.referrerProductId);
  // Pin the pre-state primer to the default catalog so a persisted insured zip can't
  // hide a state-restricted referrer product and bounce the user out of the flow.
  const catalogQuery = useGetProductCatalogQuery(DEFAULT_CATALOG_STATE);

  const product = useMemo(
    () => catalogQuery.data?.find((catalogProduct) => catalogProduct.uuid === referrerProductId),
    [catalogQuery.data, referrerProductId]
  );

  return {
    referrerProductId,
    product,
    isLoading: catalogQuery.isLoading,
    // Gated on `isSuccess` so a failed catalog request (recoverable) isn't
    // mistaken for an invalid link and doesn't clear `referrerProductId`.
    isNotFound: Boolean(referrerProductId) && catalogQuery.isSuccess && !product,
  };
}
